import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, UserCheck, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATI, PRIORITA } from "@/lib/api";
import { useFetch, useUsers, useClientsList, usePaged } from "@/lib/hooks";
import { Pager } from "@/components/core/Pager";
import { PageHeader, Panel } from "@/components/core/AppLayout";
import { TaskTable } from "@/components/core/TaskTable";
import { TaskFormDialog } from "@/components/core/TaskFormDialog";
import { TaskBoard } from "@/components/core/TaskBoard";
import { MobileFilters } from "@/components/core/MobileFilters";
import { ViewToggle } from "@/components/core/ViewToggle";

const Filter = ({ value, onChange, options, all, testid, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-[180px] rounded-xl bg-[#A6DAFC]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function TasksPage() {
  const [sp] = useSearchParams();
  const [mine, setMine] = useState(sp.get("mine") === "1");
  const [socio, setSocio] = useState("all");
  const [stato, setStato] = useState("all");
  const [cliente, setCliente] = useState("all");
  const [prio, setPrio] = useState("all");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("core_task_view") || "elenco");
  useEffect(() => localStorage.setItem("core_task_view", view), [view]);
  const { data: users } = useUsers();
  const { data: clients } = useClientsList();

  const p = new URLSearchParams();
  if (mine) p.set("mine", "true");
  else if (socio !== "all") p.set("assignee_id", socio);
  if (stato !== "all") p.set("stato", stato);
  if (cliente !== "all") p.set("client_id", cliente);
  if (prio !== "all") p.set("priorita", prio);
  if (dq) p.set("q", dq);
  const board = view === "bacheca";
  const { data: boardTasks, reload: reloadBoard } = useFetch(board ? `/tasks?${p.toString()}` : null);
  const paged = usePaged(board ? null : "/tasks", Object.fromEntries(p), 50);
  const tasks = board ? boardTasks : paged.items;
  const reload = board ? reloadBoard : paged.reload;
  const shown = tasks || [];
  const total = board ? shown.length : paged.data?.total ?? 0;

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow="Operatività" title="Task" subtitle={tasks ? `${total} task` + (board ? ` · ${shown.filter((t) => t.scaduta).length} scadute` : "") : " "}
        actions={<Button onClick={() => setOpen(true)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] h-11 px-6 font-bold" data-testid="add-task-button"><Plus className="h-4 w-4 mr-1" />Nuova task</Button>} />
      <div className="flex flex-wrap items-center gap-3 mb-5 core-rise core-delay-1">
        <ViewToggle value={view} onChange={setView} />
        <button onClick={() => setMine((m) => !m)} data-testid="task-filter-my-tasks"
          className={`h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-2 border transition-colors ${mine ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-[#A6DAFC] text-[#0A1128] border-slate-200 hover:border-[#0A1128]"}`}>
          <UserCheck className="h-4 w-4" /> Le mie task
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca task..." className="pl-9 w-[220px] max-w-full rounded-xl bg-[#A6DAFC]" data-testid="task-search-input" />
        </div>
        <MobileFilters testid="task-filters">
        <Filter value={socio} onChange={setSocio} all="Tutti i soci" testid="task-filter-socio" disabled={mine}
          options={[{ value: "none", label: "Non assegnate" }, ...(users || []).map((u) => ({ value: u.id, label: `${u.nome} ${u.cognome}` }))]} />
        <Filter value={stato} onChange={setStato} all="Tutti gli stati" testid="task-filter-stato" options={STATI} />
        <Filter value={cliente} onChange={setCliente} all="Tutti i clienti" testid="task-filter-cliente" options={(clients || []).map((c) => ({ value: c.id, label: c.ragione_sociale }))} />
        <Filter value={prio} onChange={setPrio} all="Tutte le priorità" testid="task-filter-priorita" options={PRIORITA} />
        </MobileFilters>
      </div>
      {view === "bacheca" ? (
        <div className="core-rise core-delay-2">
          <TaskBoard tasks={tasks ? shown : []} onChanged={reload} users={users || []} clients={clients || []} />
        </div>
      ) : (
        <Panel className="overflow-hidden core-rise core-delay-2">
          <TaskTable tasks={tasks ? shown : null} onChanged={reload} users={users || []} clients={clients || []} emptyText={total === 0 && !dq && p.toString() === "" ? "Crea la prima task: assegnala a un socio, collegala a un cliente e dalle una scadenza." : "Nessuna task corrisponde ai filtri selezionati."}
            emptyAction={!(clients || []).length ? <Button asChild variant="outline" className="rounded-full font-bold" data-testid="tasks-empty-clients"><Link to="/clienti">Prima crea un cliente</Link></Button>
              : <Button onClick={() => setOpen(true)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="tasks-empty-new"><Plus className="h-4 w-4 mr-1" />Nuova task</Button>} />
          <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="tasks-pager" />
        </Panel>
      )}
      <TaskFormDialog open={open} onOpenChange={setOpen} users={users || []} clients={(clients || []).filter((c) => c.stato === "attivo")} onSaved={reload} />
    </div>
  );
}
